import {useCallback, useEffect, useState} from "react";
import {
  askUserPermission,
  createNotificationSubscription,
  getUserSubscription,
  isPushNotificationSupported,
  sendSubscriptionToServer,
} from "../services/pushNotification.service";

const usePushNotifications = userId => {
  // Safely check for Notification API and get initial permission state
  const getInitialPermissionState = () => {
    if (typeof Notification !== "undefined") {
      return Notification.permission;
    }
    return "default"; // Default fallback when Notification API is not available
  };

  // To manage the push notification subscription status
  const [userConsent, setUserConsent] = useState(getInitialPermissionState());
  const [subscription, setSubscription] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [error, setError] = useState(null);
  const [pushServerError, setPushServerError] = useState(null);
  const [pushSupported, setPushSupported] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported by the browser
    const supported = isPushNotificationSupported();
    setPushSupported(supported);

    if (supported) {
      // Check for existing subscriptions
      getUserSubscription()
        .then(existingSubscription => {
          setSubscription(existingSubscription);

          // If user is already subscribed and we have their userId, make sure the server knows
          if (existingSubscription && userId) {
            sendSubscriptionToServer(existingSubscription, userId).catch(error => {
              setPushServerError(error.message);
            });
          }
        })
        .catch(error => {
          setError(error.message);
        });
    }
  }, [userId]);

  /**
   * Request permission to send push notifications
   */
  const onRequestPermission = async () => {
    setError(null);
    setIsSubscribing(true);

    try {
      const consent = await askUserPermission();
      setUserConsent(consent);

      if (consent === "granted") {
        const newSubscription = await createNotificationSubscription();
        setSubscription(newSubscription);

        if (userId) {
          await sendSubscriptionToServer(newSubscription, userId);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubscribing(false);
    }
  };

  /**
   * Send an existing subscription to the server (e.g., when a user logs in)
   * Using useCallback to memoize the function and avoid dependency issues
   */
  const onSendSubscriptionToServer = useCallback(async () => {
    if (!subscription || !userId) {
      return;
    }

    try {
      setPushServerError(null);
      await sendSubscriptionToServer(subscription, userId);
    } catch (error) {
      setPushServerError(error.message);
    }
  }, [subscription, userId]);

  // Handle changes in userId (e.g., user logs in)
  useEffect(() => {
    if (subscription && userId) {
      onSendSubscriptionToServer();
    }
  }, [userId, subscription, onSendSubscriptionToServer]);

  return {
    pushSupported,
    userConsent,
    subscription,
    onRequestPermission,
    isSubscribing,
    error,
    pushServerError,
  };
};

export default usePushNotifications;
